/**
 * External dependencies
 */
import { useState, useEffect } from "react";
import { Box, Text } from "@towersystems/towersystems-design-system";
import page from "page";
import {
  map,
  get,
  filter,
  lowerCase,
  includes,
  compact,
  size,
  omitBy,
  isEmpty,
} from "lodash";
import { connect } from "react-redux";
import queryString from "querystring";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import { TbFilterSearch } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { ButtonV2 } from "components/button-v2";
import { FiPlus } from "react-icons/fi";

/**
 * Internal dependencies
 */
import Main from "components/main";
import DocumentHead from "data/document-head";
import PageHeader from "layout/page-header";
import Button from "components/button";
import libraryReports from "lib/report-library";
import ReportList from "modules/reports/report-list";
import { saveReport } from "state/reports/actions";
import Navigation from "modules/reports/navigation";
import DropdownSelect from "components/dropdown-select";
import ReportFilters from "modules/reports/report-filters";
import TagGroup from "components/tag/tag-group";
import Tag from "components/tag";
import { startEditingReport } from "state/ui/report-builder/actions";
import { successNotice } from "state/notices/actions";
/**
 * Style dependencies
 */
import "./styles.scss";
import SelectReportDropdownSelect from "blocks/switch-report-dropdown-select";

const ReportLibrary = ({ ...props }) => {
  const isMobile = useMediaQuery({ maxWidth: "1150px" });

  const [query, setQuery] = useState(props.query || "");
  const [filterCustomized, setFilterCustomized] = useState(false);
  const [dataSetFilters, setDataSetFilters] = useState(props.dataSetFilters);
  const [dataTypeFilters, setDataTypeFilters] = useState(props.dataTypeFilters);
  const [isMenuOpen, setShowMenu] = useState(false);

  const toggleMenu = () => {
    setShowMenu(!isMenuOpen);
  };

  useEffect(() => {
    updatePath();

    // need to simulate a scroll otherwise some of the reports
    // will not render.. due to a module which only renders components when a
    // scroll is detected
    window.scroll(0, 1);
    window.scroll(0, -1);
  }, [dataSetFilters, dataTypeFilters, query]);

  const updatePath = () => {
    let currentPath = "/reports/report-library";
    let q = queryString.stringify(
      omitBy({ dataSetFilters, dataTypeFilters, query }, (v) => isEmpty(v)),
    );

    if (q) {
      return page(`${currentPath}?${q}`);
    } else {
      return page(`${currentPath}`);
    }
  };

  const filterReports = (reports) => {
    let filtered = filter(reports, (report) => {
      if (filterCustomized === true) {
        return get(report, "report.template") === "custom";
      }
      return true;
    });

    filtered = filter(filtered, (report) => {
      return lowerCase(get(report, "report.name", "")).includes(
        lowerCase(query),
      );
    });

    // filter the data sets
    filtered = filter(filtered, (report) => {
      return (
        size(dataTypeFilters) === 0 ||
        includes(dataTypeFilters, get(report, "report.config.dataType"))
      );
    });

    filtered = filter(filtered, (report) => {
      return (
        size(dataSetFilters) === 0 ||
        includes(dataSetFilters, get(report, "report.config.dataSet"))
      );
    });

    return filtered;
  };

  const reports = map(filterReports([...libraryReports]), ({ id, report }) => ({
    key: id,
    name: report.name,
    report,
    actions: compact([
      {
        key: "save",
        onClick: async () => {
          await props.saveReport(null, report);
          props.successNotice("Report has been added to your dashboard.");
        },
        label: "Add Active Deck",
        compact: true,
        variant: "outline",
        elevation: 1,
      },
      report.template === "custom" && {
        key: "edit",
        onClick: () => {
          props.startEditingReport(null, report);
          page("/report-builder/new");
        },
        label: "Edit view",
        variant: "outline",
        compact: true,
        elevation: 1,
      },
    ]),
  }));

  const tags = [...dataSetFilters, ...dataTypeFilters];
  const showTags = size(tags) >= 1;

  const handleReportFilterChange = ({ dataSetFilters, dataTypeFilters }) => {
    setDataSetFilters(dataSetFilters);
    setDataTypeFilters(dataTypeFilters);
  };

  return (
    <Box>
      <PageHeader
        title={isMobile ? undefined : "View Library"}
        actions={
          <Box
            width={isMobile ? "full" : "auto"}
            className="flex gap-3 items-center"
          >
            <Box
              style={{
                flexGrow: 1,
              }}
            >
              <input
                className="input input--white"
                type="search"
                placeholder="Search the report library"
                onChange={(e) => setQuery(e.target.value)}
                value={query}
                style={{ width: "100%", marginRight: 15 }}
              />
            </Box>
            <Box>
              <ButtonV2
                variant="primary"
                onClick={() => page("/report-builder/new")}
                leftIcon={<FiPlus size={20} />}
              >
                Create view
              </ButtonV2>
            </Box>
          </Box>
        }
      />
      <DocumentHead title="Views - View Library" />
      <Main
        className={classNames("report__content", isMobile && "mobile-view")}
      >
        <Box
          className={classNames(
            "report__content-left",
            isMenuOpen && "is-open",
          )}
        >
          {isMobile ? (
            <Box
              marginRight="large"
              display="flex"
              justifyContent="flexEnd"
              paddingY="large"
            >
              <Button
                compact
                className="topbar__toggle"
                id="topbar-toggle"
                onClick={toggleMenu}
              >
                <IoMdClose size={20} />
              </Button>
            </Box>
          ) : null}

          <Navigation selected="report-library" />
          <div className="mt-12">
            <ReportFilters
              defaultDataTypeFilters={dataTypeFilters}
              defaultDataSetFilters={dataSetFilters}
              onChange={handleReportFilterChange}
            />
          </div>
        </Box>
        <Box className="report__content-right">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="spaceBetween"
            marginBottom="large"
          >
            <Box display="flex" alignItems="center">
              {isMobile ? (
                <Box marginRight="large">
                  <Button
                    compact
                    className="topbar__toggle"
                    id="topbar-toggle"
                    onClick={toggleMenu}
                  >
                    <TbFilterSearch size={20} />
                  </Button>
                </Box>
              ) : null}
              <Text size="small">
                Showing <span className="bold">{size(reports)}</span> results
              </Text>
            </Box>
            <div className="flex gap-3">
              <SelectReportDropdownSelect />
              <DropdownSelect
                selected="all"
                onChange={(v) => setFilterCustomized(v === "customizable")}
                options={[
                  { key: "all", value: "all", label: "All views" },
                  {
                    key: "customizable",
                    value: "customizable",
                    label: "Only customizable",
                  },
                ]}
              />
            </div>
          </Box>
          {showTags && (
            <Box marginBottom="large">
              <TagGroup>
                {map(tags, (f) => (
                  <Tag key={f} label={f} />
                ))}
              </TagGroup>
            </Box>
          )}
          <ReportList reports={reports} />
        </Box>
      </Main>
    </Box>
  );
};

export default connect(null, { saveReport, startEditingReport, successNotice })(
  ReportLibrary,
);
